import { useEffect, useRef } from "react";

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  /** Stagger in ms, for lists of siblings. */
  delay?: number;
  /** Render as a different element — useful for list rows. */
  as?: "div" | "li" | "section";
}

/**
 * Fades content up as it scrolls into view.
 *
 * Unlike a naive IntersectionObserver reveal, this never leaves content stuck
 * invisible:
 *   - the hidden class is only applied if the element starts out off-screen, so
 *     deep-linking to #experience or landing mid-page shows content immediately;
 *   - a safety timer reveals everything after 1.2s no matter what, so a missed
 *     observer callback can't blank out a section.
 */
const Reveal = ({ children, className = "", delay = 0, as = "div" }: RevealProps) => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    // Already visible on mount (direct link, restored scroll) — don't hide it.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) return;

    el.classList.add("js-reveal");

    const show = () => {
      el.style.transitionDelay = delay ? `${delay}ms` : "";
      el.classList.add("js-reveal-in");
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          show();
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0 }
    );
    observer.observe(el);

    // Safety net: never let content stay hidden.
    const failsafe = window.setTimeout(() => {
      show();
      observer.disconnect();
    }, 1200);

    return () => {
      observer.disconnect();
      window.clearTimeout(failsafe);
    };
  }, [delay]);

  const Tag = as as "div";
  return (
    <Tag ref={ref as React.RefObject<HTMLDivElement>} className={className}>
      {children}
    </Tag>
  );
};

export default Reveal;
